import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Building2, ShieldCheck, KeyRound, ChevronDown, Home, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, errMsg } from "../lib/api";
import { useAuth, homePathFor } from "../context/AuthContext";
import { ForgotPasswordDialog, ForgotPasswordLink } from "../components/ForgotPasswordDialog";
import { SuperOtpStep, getSuperDeviceToken, setSuperDeviceToken } from "../components/SuperOtpStep";

export const ALL_DEMO_CREDENTIALS = [
  {
    group: "Bhilai Municipal Corporation (BMC)", muni: "bmc",
    items: [
      { role: "Municipal Admin", email: "admin@bhilai.gov.in", password: "Admin@123", kind: "staff" },
      { role: "Supervisor", email: "supervisor@bhilai.gov.in", password: "Super@123", kind: "staff" },
      { role: "Tax Collector", email: "collector@bhilai.gov.in", password: "Collect@123", kind: "staff" },
      { role: "Citizen", email: "citizen@bhilai.gov.in", password: "Citizen@123", kind: "citizen" },
    ],
  },
  {
    group: "Nagar Nigam Ghaziabad (GNN-01)", muni: "gzb",
    items: [
      { role: "Municipal Admin", email: "admin@ghaziabad.gov.in", password: "Admin@123", kind: "staff" },
      { role: "Supervisor", email: "supervisor@ghaziabad.gov.in", password: "Super@123", kind: "staff" },
      { role: "Tax Collector", email: "collector@ghaziabad.gov.in", password: "Collect@123", kind: "staff" },
      { role: "Citizen", email: "citizen@ghaziabad.gov.in", password: "Citizen@123", kind: "citizen" },
    ],
  },
  {
    group: "Central Platform", muni: null,
    items: [
      { role: "Super Admin", email: "superadmin@municipalconnect.gov.in", password: "SuperAdmin@123", kind: "super" },
    ],
  },
];

export function AllDemoCredentials({ onPick, testId = "all-demo-credentials" }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-4 border border-dashed rounded-lg bg-muted/50" data-testid={testId}>
      <CollapsibleTrigger asChild>
        <button type="button"
          className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          data-testid={`${testId}-trigger`}>
          <span className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Demo credentials — all municipalities</span>
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        <Accordion type="multiple" className="space-y-1.5">
          {ALL_DEMO_CREDENTIALS.map((g) => (
            <AccordionItem key={g.group} value={g.group} className="border rounded-md bg-background px-2.5">
              <AccordionTrigger className="py-2 text-xs font-semibold hover:no-underline" data-testid={`demo-group-${g.muni || "platform"}`}>
                {g.group}
              </AccordionTrigger>
              <AccordionContent className="pb-2">
                <div className="space-y-0.5">
                  {g.items.map((c) => (
                    <button key={c.email} type="button" onClick={() => onPick({ ...c, muni: g.muni })}
                      className="w-full flex items-center justify-between gap-2 text-left rounded px-2 py-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`demo-cred-${g.muni || "platform"}-${c.role.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="text-xs font-medium shrink-0">{c.role}</span>
                      <span className="text-[11px] text-muted-foreground font-mono truncate">{c.email} / {c.password}</span>
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="text-[10px] text-muted-foreground mt-2">Tap any entry to autofill the matching login form.</p>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function Login() {
  const [munis, setMunis] = useState([]);
  const [muniCode, setMuniCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saEmail, setSaEmail] = useState("");
  const [saPassword, setSaPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("staff");
  const [fpStaffOpen, setFpStaffOpen] = useState(false);
  const [fpSuperOpen, setFpSuperOpen] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null); // Phase 154: Super Admin two-step sign-in
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Phase 154: a Super Admin sign-in answers with a session (trusted browser / OTP not deliverable) or an OTP challenge
  const finishSuperLogin = (data) => {
    if (data.two_step === "required") {
      setTab("super");
      setOtpChallenge(data);
      return;
    }
    if (data.device_token) setSuperDeviceToken(data.device_token);
    const authData = { token: data.token, user: data.user, municipality: null };
    login(authData);
    if (data.two_step === "skipped") {
      toast.warning("Signed in without the SMS code — two-step sign-in is not active", { description: data.two_step_reason_text });
    } else {
      toast.success("Welcome, Super Admin");
    }
    navigate("/super");
  };

  const handlePick = (c) => {
    if (c.kind === "citizen") {
      navigate("/citizen", { state: { demo: c } });
      return;
    }
    if (c.kind === "super") {
      setTab("super");
      setSaEmail(c.email);
      setSaPassword(c.password);
      toast.info("Filled Super Admin credentials — press Sign in");
      return;
    }
    setTab("staff");
    if (c.muni) setMuniCode(c.muni);
    setEmail(c.email);
    setPassword(c.password);
    toast.info(`Filled ${c.role} (${(c.muni || "").toUpperCase()}) credentials — press Sign in`);
  };

  useEffect(() => {
    if (location.state?.demo) handlePick(location.state.demo);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => {
      setMunis(r.data);
      if (r.data.length === 1) setMuniCode(r.data[0].code);
    }).catch(() => {});
  }, []);

  const staffLogin = async (e) => {
    e.preventDefault();
    if (!muniCode) {
      // Fallback: platform users (super admins) don't need a municipality.
      // Try platform login before blocking on municipality selection.
      setLoading(true);
      try {
        const r = await api.post("/platform/auth/login", { email, password, device_token: getSuperDeviceToken() });
        finishSuperLogin(r.data);
      } catch {
        toast.error("Select your municipality");
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/t/${muniCode}/auth/login`, { email, password });
      // Phase 174: `roles` lists every role account this person holds; the server already picked the last-used one
      const roles = Array.isArray(r.data.roles) ? r.data.roles : [];
      const authData = { token: r.data.token, user: r.data.user, municipality: r.data.municipality, roles };
      login(authData);
      const activeRoles = roles.filter((x) => x.is_active !== false);
      const current = activeRoles.find((x) => x.is_current);
      if (activeRoles.length > 1 && current) {
        toast.success(`Welcome, ${r.data.user.name}`, {
          description: `Signed in as ${current.label} — you also hold ${activeRoles.filter((x) => !x.is_current).map((x) => x.label).join(", ")}. Switch roles from the header.`,
        });
      } else {
        toast.success(`Welcome, ${r.data.user.name}`);
      }
      navigate(homePathFor(authData));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const superLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/platform/auth/login", { email: saEmail, password: saPassword, device_token: getSuperDeviceToken() });
      finishSuperLogin(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen civic-mist flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <Button asChild variant="ghost" size="sm" className="h-9 px-2.5 -ml-2.5" data-testid="login-home-link">
            <Link to="/"><Home className="h-4 w-4 mr-1.5" /> Home</Link>
          </Button>
          <Button asChild variant="ghost" size="sm" className="h-9 px-2.5 text-muted-foreground" data-testid="login-citizen-portal-link">
            <Link to="/citizen">Citizen Portal <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
        <div className="flex items-center gap-2 justify-center mb-6 cursor-pointer" onClick={() => navigate("/")}>
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <p className="font-semibold font-heading">MunicipalConnect</p>
            <p className="text-xs text-muted-foreground">Staff &amp; Platform Access</p>
          </div>
        </div>
        <Card className="shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Sign in</CardTitle>
            <CardDescription>Municipal staff or platform super admin</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="staff" data-testid="tab-staff-login">Municipal Staff</TabsTrigger>
                <TabsTrigger value="super" data-testid="tab-super-login">Super Admin</TabsTrigger>
              </TabsList>
              <TabsContent value="staff">
                <form onSubmit={staffLogin} className="space-y-4 mt-2">
                  <div className="space-y-1.5">
                    <Label>Municipality</Label>
                    <Select value={muniCode} onValueChange={setMuniCode}>
                      <SelectTrigger data-testid="login-municipality-select">
                        <SelectValue placeholder="Select municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {munis.map((m) => (
                          <SelectItem key={m.code} value={m.code} data-testid={`login-municipality-option-${m.code}`}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@municipality.gov.in" required data-testid="login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <ForgotPasswordLink onClick={() => setFpStaffOpen(true)} testId="staff-forgot-password-link" />
                    </div>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      required data-testid="login-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="super">
                {otpChallenge ? (
                  <SuperOtpStep challenge={otpChallenge} onBack={() => setOtpChallenge(null)}
                    onVerified={(data) => { setOtpChallenge(null); finishSuperLogin(data); }} />
                ) : (
                <form onSubmit={superLogin} className="space-y-4 mt-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">
                    <ShieldCheck className="h-4 w-4" /> Central platform administration access
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sa-email">Email</Label>
                    <Input id="sa-email" type="email" value={saEmail} onChange={(e) => setSaEmail(e.target.value)}
                      required data-testid="super-login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sa-password">Password</Label>
                      <ForgotPasswordLink onClick={() => setFpSuperOpen(true)} testId="super-forgot-password-link" />
                    </div>
                    <Input id="sa-password" type="password" value={saPassword} onChange={(e) => setSaPassword(e.target.value)}
                      required data-testid="super-login-password-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="super-login-submit-button">
                    {loading ? "Signing in..." : "Sign in as Super Admin"}
                  </Button>
                </form>
                )}
              </TabsContent>
            </Tabs>
            <AllDemoCredentials onPick={handlePick} testId="all-demo-credentials" />
          </CardContent>
        </Card>
        <ForgotPasswordDialog open={fpStaffOpen} onOpenChange={setFpStaffOpen}
          variant="tenant" muniCode={muniCode} defaultEmail={email} />
        <ForgotPasswordDialog open={fpSuperOpen} onOpenChange={setFpSuperOpen}
          variant="platform" defaultEmail={saEmail} />
      </div>
    </div>
  );
}
